import { Fragment, type ReactNode } from 'react';
import { AutoComplete, Button, Input, Space, Tag, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { ADDRESS_LABELS, ADDRESS_PART_LABELS, ADDRESS_PARTS, isEmptyAddress, type Address, type AddressPart } from '@crm/shared';
import { palette } from '../app/theme';

export const EMPTY_ADDRESS: Address = { label: null, line1: null, line2: null, city: null, state: null, country: null, postalCode: null };

/** One address on a few lines, the way it is written on an envelope. */
export function AddressBlock({ address: a, showLabel = true }: { address: Partial<Address>; showLabel?: boolean }) {
  const cityLine = [a.city, a.state, a.postalCode].filter(Boolean).join(', ');
  return (
    <div style={{ lineHeight: 1.45 }}>
      {showLabel && a.label && <Tag style={{ marginBottom: 2 }}>{a.label}</Tag>}
      {a.line1 && <div>{a.line1}</div>}
      {a.line2 && <div>{a.line2}</div>}
      {cityLine && <div>{cityLine}</div>}
      {a.country && <div>{a.country}</div>}
    </div>
  );
}

/** A record's addresses, or a dash when it has none. */
export function AddressList({ items }: { items: Address[] | undefined }) {
  if (!items?.length) return <Typography.Text type="secondary">—</Typography.Text>;
  return (
    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
      {items.map((a, i) => (
        <AddressBlock key={i} address={a} />
      ))}
    </Space>
  );
}

const PLACEHOLDERS: Record<AddressPart, string> = {
  line1: 'Street and number',
  line2: 'Apartment, suite, building',
  city: 'City',
  state: 'State, county or district',
  country: 'Country',
  postalCode: 'ZIP / postal code',
};
const MAX: Record<AddressPart, number> = { line1: 300, line2: 300, city: 120, state: 120, country: 120, postalCode: 30 };

/** Inputs for one address: a label and the six parts. */
export function AddressFields({ value, onChange, index }: { value: Address; onChange: (a: Address) => void; index?: number }) {
  const n = index !== undefined ? ` ${index + 1}` : '';
  const set = (p: Partial<Address>) => onChange({ ...value, ...p });
  const caption = (text: string, node: ReactNode, wide = false) => (
    <div style={{ gridColumn: wide ? '1 / -1' : undefined, minWidth: 0 }}>
      <div style={{ fontSize: 12, color: palette.textSecondary, marginBottom: 2 }}>{text}</div>
      {node}
    </div>
  );
  const input = (p: AddressPart) =>
    caption(
      ADDRESS_PART_LABELS[p],
      <Input aria-label={`${ADDRESS_PART_LABELS[p]}${n}`} placeholder={PLACEHOLDERS[p]} value={value[p] ?? ''} maxLength={MAX[p]} onChange={(e) => set({ [p]: e.target.value })} />,
      p === 'line1' || p === 'line2',
    );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
      {caption(
        'Label',
        <AutoComplete
          aria-label={`Label for address${n}`}
          placeholder="e.g. Work"
          value={value.label ?? ''}
          onChange={(v: string) => set({ label: v })}
          options={ADDRESS_LABELS.map((l) => ({ value: l }))}
          filterOption={false}
          maxLength={40}
          style={{ width: '100%', maxWidth: 200 }}
        />,
        true,
      )}
      {ADDRESS_PARTS.map((p) => (
        <Fragment key={p}>{input(p)}</Fragment>
      ))}
    </div>
  );
}

/** Editor for a record's addresses (a Form.Item child: takes value / onChange). */
export function AddressesInput({ value, onChange }: { value?: Address[]; onChange?: (list: Address[]) => void }) {
  const list = value ?? [];
  const set = (next: Address[]) => onChange?.(next);
  return (
    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
      {list.map((a, i) => (
        <div key={i} style={{ border: `1px solid ${palette.border}`, borderRadius: 8, padding: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <AddressFields value={a} index={i} onChange={(next) => set(list.map((x, j) => (j === i ? next : x)))} />
          </div>
          <Button type="text" aria-label={`Remove address ${i + 1}`} icon={<DeleteOutlined />} onClick={() => set(list.filter((_, j) => j !== i))} />
        </div>
      ))}
      <Button icon={<PlusOutlined />} onClick={() => set([...list, { ...EMPTY_ADDRESS }])}>
        Add address
      </Button>
    </Space>
  );
}

/** The editor's rows as the API takes them: blank addresses dropped. */
export const toAddressesPayload = (list: Address[] | undefined) => (list ?? []).filter((a) => !isEmptyAddress(a));

const SHORT_LABELS: Record<AddressPart, string> = { line1: 'Line 1', line2: 'Line 2', city: 'City', state: 'State / County', country: 'Country', postalCode: 'ZIP / Postal' };

/** One address part by part, each with its name: for reviewing how an address was split. */
export function AddressParts({ address: a }: { address: Partial<Address> }) {
  return (
    <div style={{ lineHeight: 1.5 }}>
      {a.label && <Tag style={{ marginBottom: 2 }}>{a.label}</Tag>}
      {ADDRESS_PARTS.filter((p) => a[p]).map((p) => (
        <div key={p}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {SHORT_LABELS[p]}:{' '}
          </Typography.Text>
          {a[p]}
        </div>
      ))}
    </div>
  );
}
